import { create } from 'zustand';
import type { Question, UserAnswer, Result } from '../types/assessment.types';

type AssessmentStatus = 'idle' | 'loading' | 'active' | 'saving' | 'finalizing' | 'completed' | 'error' | 'already_completed';

export interface AssessmentState {
  assessmentId: string | null;
  questions: Question[];
  answers: UserAnswer[];
  currentQuestionIndex: number;
  status: AssessmentStatus;
  result: Result | null;
  actions: AssessmentActions;
}

interface AssessmentActions {
  start: (assessmentId: string, questions: Question[]) => void;
  resume: (assessmentId: string, questions: Question[], answers: UserAnswer[], progress: number) => void;
  answerQuestion: (answer: UserAnswer) => void;
  nextQuestion: () => void;
  previousQuestion: () => void;
  setSaving: () => void;
  setSavingComplete: () => void;
  complete: (result: Result) => void;
  setResult: (result: Result) => void; // Adicionado para compatibilidade
  reset: () => void;
  setStatus: (status: AssessmentStatus) => void;
  setFinalizing: () => void;
}

const initialState = {
    assessmentId: null,
    questions: [],
    answers: [],
    currentQuestionIndex: 0,
    status: 'idle' as AssessmentStatus,
    result: null,
};

export const useAssessmentStore = create<AssessmentState>((set, get) => ({
  ...initialState,
  actions: {
    start: (assessmentId, questions) => set({ 
      assessmentId, 
      questions, 
      status: 'active', 
      currentQuestionIndex: 0, 
      answers: [],
      result: null,
    }),
    resume: (assessmentId, questions, answers, progress) => {
      const answeredQuestionIds = answers.map(a => a.question_id);
      const currentIndex = Math.min(answeredQuestionIds.length, questions.length - 1);
      
      set({ 
        assessmentId, 
        questions, 
        status: 'active', 
        currentQuestionIndex: currentIndex, 
        answers,
        result: null,
      });
    },
    answerQuestion: (newAnswer) => {
        set(state => {
            const existingAnswerIndex = state.answers.findIndex(a => a.question_id === newAnswer.question_id);
            const newAnswers = [...state.answers];
            if (existingAnswerIndex > -1) {
                newAnswers[existingAnswerIndex] = newAnswer;
            } else {
                newAnswers.push(newAnswer);
            }
            return { answers: newAnswers };
        });
    },
    nextQuestion: () => {
        const { currentQuestionIndex, questions } = get();
        if (currentQuestionIndex < questions.length - 1) {
            set({ currentQuestionIndex: currentQuestionIndex + 1 });
        }
    },
    previousQuestion: () => {
        const { currentQuestionIndex } = get();
        if (currentQuestionIndex > 0) {
            set({ currentQuestionIndex: currentQuestionIndex - 1 });
        }
    },
    setSaving: () => {
        const { status } = get();
        if(status === 'active') {
          set({ status: 'saving' });
        }
    },
    setSavingComplete: () => {
      const { status } = get();
      if(status === 'saving') {
        set({ status: 'active' });
      }
    },
    setFinalizing: () => set({ status: 'finalizing' }),
    complete: (result) => set({ result, status: 'completed' }),
    setResult: (result) => set({ result }), // Implementação para compatibilidade
    reset: () => set({ ...initialState }),
    setStatus: (status) => set({ status }),
  }
})); 